import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, useParams } from "@tanstack/react-router";
import { Laptop01 } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { EmptyValue } from "@/components/common";
import { TruncatedTooltipCellText } from "@/components/common/TruncatedTooltipCellText";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	SessionTableFilters,
	type SessionTableFiltersState,
} from "@/components/Molecules/FilterDrawer/SessionTableFilters";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { RevokeSessionsModal } from "@/components/Organisms/RevokeSessionsModal";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDebounced } from "@/hooks/use-debounced";
import { useTableSettings } from "@/hooks/use-table-settings";
import {
	type UserSession,
	useExportUserSessions,
	useRevokeSessions,
	userSessionQueryOptions,
} from "@/services/user-session";
import type { FilterConfig } from "@/utils/build-filter-params";
import { buildFilterParams } from "@/utils/build-filter-params";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { formatLocation } from "@/utils/format-location";
import { formatLongDate } from "@/utils/utils";

// Table settings constants
const TABLE_KEY = "orgAdmin.activity.sessions";
const DEFAULT_COLUMN_ORDER = [
	"userName",
	"sessionStartedAt",
	"userAgent",
	"userIpAddress",
	"location",
	"expirationTimestamp",
];
const DEFAULT_SORTING = [{ id: "userName", desc: false }];
const DEFAULT_PAGE_SIZE = 10;

const normalizeColumnOrder = (columnOrder?: string[]) => {
	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...DEFAULT_COLUMN_ORDER];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter((columnId) =>
		allowedColumns.has(columnId),
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults];
};

// This table only has date range filters which are not persisted
// so we use an empty PersistedFilters interface
// biome-ignore lint/suspicious/noEmptyInterface: No serializable filters for this table
interface PersistedFilters {}
const DEFAULT_FILTERS: PersistedFilters = {};

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/activity/(sessions)/sessions",
)({
	component: SessionsTableComponent,
	validateSearch: (search) => {
		return z
			.object({
				filterOpen: z.boolean().optional(),
			})
			.parse(search);
	},
});

function SessionsTableComponent() {
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/activity/(sessions)/sessions",
	});
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
	const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);
	const revokeMutation = useRevokeSessions();
	const [filters, setFilters] = useState<SessionTableFiltersState>({
		loginTimeRange: null,
		timeRemainingRange: null,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["User.LastName", "User.FirstName"],
		"OrderBy.Descending": false,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (search, page size) when config has loaded
	// Note: This table only has date range filters which are not persisted
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [settingsLoaded, tableSettings.searchTerm, tableSettings.pageSize]);

	const sortFieldMapping: Record<string, string[]> = {
		userName: ["User.LastName", "User.FirstName"],
		sessionStartedAt: ["SessionStartedAt"],
		userAgent: ["UserAgent"],
		userIpAddress: ["UserIpAddress"],
		location: ["Location"],
		expirationTimestamp: ["ExpirationTimestamp"],
	};
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportUserSessions();
	const debouncedFilters = useDebounced(filters, 500);
	const now = Date.now();

	const filterConfig: FilterConfig = {
		"loginTimeRange.start": {
			paramName: "SessionStartedAfter",
			transform: "dateStart",
		},
		"loginTimeRange.end": {
			paramName: "SessionStartedBefore",
			transform: "dateEnd",
			dateStartField: "loginTimeRange.start",
		},
		"timeRemainingRange.minMinutes": {
			paramName: "ExpirationAfter",
			transform: "durationToDateAfter",
		},
		"timeRemainingRange.maxMinutes": {
			paramName: "ExpirationBefore",
			transform: "durationToDateBefore",
		},
	};

	const filterParams = useMemo(() => {
		return buildFilterParams(debouncedFilters, filterConfig);
	}, [debouncedFilters]);

	const _sessionCount = useQuery(
		userSessionQueryOptions.count({
			OrganizationId: params.orgId,
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const unFilteredSessionCount = useQuery(
		userSessionQueryOptions.count({ OrganizationId: params.orgId }),
	);

	const orgScopedUserSessions = useQuery(
		userSessionQueryOptions.list(startIndex, pageSize, {
			OrganizationId: params.orgId,
			SearchTerm: searchTerm,
			...filterParams,
			...sortParams,
		}),
	);

	const data = useMemo(() => {
		return orgScopedUserSessions.data ?? [];
	}, [orgScopedUserSessions.data]);

	const pageCount = useMemo(() => {
		if (!_sessionCount.data) return 0;
		return Math.ceil(_sessionCount.data / pageSize);
	}, [_sessionCount.data, pageSize]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await orgScopedUserSessions.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = _sessionCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					OrganizationId: params.orgId,
					SearchTerm: searchTerm,
					...filterParams,
					...sortParams,
					columns: visibleColumns?.join(","),
				},
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("org-user-sessions"));
				},
			},
		);
	};

	const handleRowClick = (session: UserSession) => {
		console.log(session);
		if (!session) return;
	};

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (search: string) => {
		setSearchTerm(search);
		setPageIndex(0);
		// Save search term for persistence
		saveSearchTerm(search);
	};

	const handleFilterChange = (newFilters: SessionTableFiltersState) => {
		setFilters(newFilters);
		// Note: This table only has date range filters which are not persisted
	};

	// Calculate selected sessions for revoke action
	const selectedSessions = useMemo(() => {
		return Array.from(selectedKeys)
			.map((id) => data.find((session) => session.sessionId === id))
			.filter((session): session is UserSession => session !== undefined)
			.map((session) => ({
				sessionId: session.sessionId,
			}));
	}, [selectedKeys, data]);

	const handleRevokeSessions = () => {
		setIsRevokeModalOpen(false);
		if (selectedSessions.length === 0) return;
		revokeMutation.mutate(selectedSessions, {
			onSuccess: () => {
				setSelectedKeys(new Set());
				setIsRevokeModalOpen(false);
			},
		});
	};

	const sessionColumns: ColumnDef<UserSession>[] = [
		{
			id: "sessionId",
			accessorKey: "sessionId",
			header: t("org-admin.sessions.table.columns.sessionId"),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			cell: ({ row }) => {
				const session = row.original;
				return session.sessionId ? (
					<div className="flex items-center gap-3">
						<Avatar
							alt={session.sessionId}
							placeholder={<Laptop01 className="size-6 text-gray-400" />}
							size="md"
						/>
						<div className="whitespace-nowrap">
							<p className="text-sm font-medium">
								{<CopyableId value={session.sessionId} />}
							</p>
						</div>
					</div>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "userName",
			accessorKey: "userName",
			header: t("org-admin.sessions.table.columns.userName"),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			minSize: 300,
			cell: ({ row }) => {
				const session = row.original;
				return (
					<span className="text-sm font-medium flex items-center gap-2">
						<div className="flex items-center justify-center size-8 rounded-full bg-gray-100">
							<Laptop01 className="size-4 text-gray-500" />
						</div>
						{session.user?.firstName && session.user?.lastName ? (
							`${session.user?.firstName} ${session.user?.lastName}`
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "sessionStartedAt",
			accessorKey: "sessionStartedAt",
			header: t("org-admin.sessions.table.columns.loginTime"),
			enableSorting: true,
			minSize: 250,
			cell: ({ row }) => {
				const session = row.original;
				return (
					<span className="text-sm font-medium">
						{session.sessionStartedAt ? (
							formatLongDate(session.sessionStartedAt, {
								includeTime: true,
							})
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "userAgent",
			accessorKey: "userAgent",
			header: t("org-admin.sessions.table.columns.device"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => (
				<TruncatedTooltipCellText value={row.original.userAgent} />
			),
		},
		{
			id: "userIpAddress",
			accessorKey: "userIpAddress",
			header: t("org-admin.sessions.table.columns.ipAddress"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				const session = row.original;
				return (
					<span className="text-sm font-medium">
						{session.userIpAddress ? session.userIpAddress : <EmptyValue />}
					</span>
				);
			},
		},
		{
			id: "location",
			accessorKey: "location",
			header: t("org-admin.sessions.table.columns.location"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				const session = row.original;
				const formattedLocation = formatLocation(session.location);
				return (
					<span className="text-sm font-medium">
						{formattedLocation !== "Unknown" ? (
							formattedLocation
						) : (
							<EmptyValue />
						)}
					</span>
				);
			},
		},
		{
			id: "expirationTimestamp",
			accessorKey: "expirationTimestamp",
			header: t("org-admin.sessions.table.columns.timeRemaining"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				const session = row.original;
				if (!session.expirationTimestamp) return <EmptyValue />;

				const expirationDate = new Date(session.expirationTimestamp);
				const diffMs = expirationDate.getTime() - now;

				// If already expired
				if (diffMs <= 0) {
					return (
						<span className="text-sm text-gray-500">
							{t("org-admin.sessions.table.columns.expired")}
						</span>
					);
				}

				const totalMinutes = Math.floor(diffMs / (1000 * 60));
				const hours = Math.floor(totalMinutes / 60);
				const minutes = totalMinutes % 60;

				const timeRemaining =
					hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

				return <span className="text-sm font-medium">{timeRemaining}</span>;
			},
		},
	];

	const noSessions = (isSearchEmpty: boolean) => {
		if (isSearchEmpty) {
			return (
				<TableSearchEmptyState titleKey="admin.activity.sessions.table.empty.noSearchResults" />
			);
		}

		return (
			<div className="flex flex-col items-center justify-center gap-1 py-12 text-center">
				<h2 className="font-semibold text-primary text-md">
					{t("org-admin.sessions.table.empty.title")}
				</h2>
				<p className="text-sm text-tertiary">
					{t("org-admin.sessions.table.empty.description")}
				</p>
			</div>
		);
	};

	useEffect(() => {
		// Don't prefetch if we don't have a page count yet
		if (!pageCount) return;

		// Configure how many pages ahead/behind to prefetch
		// Increase this number to prefetch more pages (e.g., 2-3 for very fast navigation)
		const prefetchRange = 1;

		// Prefetch multiple pages ahead
		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					userSessionQueryOptions.list(nextStartIndex, pageSize, {
						OrganizationId: params.orgId,
						SearchTerm: searchTerm,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}

		// Prefetch multiple pages behind
		for (let i = 1; i <= prefetchRange; i++) {
			const prevPageIndex = pageIndex - i;
			if (prevPageIndex >= 0) {
				const prevStartIndex = prevPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					userSessionQueryOptions.list(prevStartIndex, pageSize, {
						OrganizationId: params.orgId,
						SearchTerm: searchTerm,
						...filterParams,
						...sortParams,
					}),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		searchTerm,
		filterParams,
		sortParams,
		queryClient,
		params.orgId,
	]);

	if (orgScopedUserSessions.isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t("org-admin.sessions.table.errors.failedToLoadSessions")}
						</h3>
						<p className="text-sm text-tertiary mt-1">
							{orgScopedUserSessions.error?.message}
						</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("admin.nav.items.activity.sessions.label")}
						description={t("admin.activity.sessions.platformDescription")}
						actions={[
							{
								label:
									selectedSessions.length > 1
										? t("admin.activity.sessions.revoke.buttonPlural")
										: t("admin.activity.sessions.revoke.button"),
								color: "primary-destructive",
								isDisabled: selectedSessions.length === 0,
								onPress: () => setIsRevokeModalOpen(true),
							},
						]}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<CustomTableWrapper
					tableKey={TABLE_KEY}
					// Data
					data={data}
					columns={sessionColumns}
					// Display
					title={t("admin.nav.items.activity.sessions.label")}
					showHeaderPadding={false}
					zebraStriping
					// Loading States
					isLoading={orgScopedUserSessions.isLoading}
					isFetching={orgScopedUserSessions.isFetching}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					// Pagination
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={handlePaginationChange}
					pageCount={pageCount}
					rowCount={_sessionCount.data}
					totalRowCount={unFilteredSessionCount.data}
					// Sorting
					sortFieldMapping={sortFieldMapping}
					defaultSortField="userName"
					onSortChange={({ fields, descending }) => {
						setSortParams({
							"OrderBy.Fields": fields,
							"OrderBy.Descending": descending,
						});
					}}
					// Filtering
					filterPlaceholder={t(
						"org-admin.activity.sessions.table.search.placeholder",
					)}
					filterValue={searchTerm}
					onDebouncedFilterChange={handleDebouncedSearchChange}
					filtersTrailingContent={
						<SessionTableFilters
							value={filters}
							onChange={handleFilterChange}
							isPlatformAdmin={false}
							organizationId={params.orgId}
						/>
					}
					// Table Actions
					enableTableActions
					filterLayout="bar"
					filterBarType="landing"
					filterBarClassName="pt-0"
					tableOptionsPortalTarget={tableOptionsHost}
					hasActiveFilters={Object.keys(filterParams).length > 0}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					// Features
					enableColumnReordering
					onRowClick={handleRowClick}
					enableColumnResizing
					// Row Selection
					enableRowSelection
					selectedKeys={selectedKeys}
					onSelectionChange={setSelectedKeys}
					getRowId={(row) => row.sessionId}
					// Persistence - Initial values from saved settings
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={tableSettings.columnVisibility}
					settingsLoaded={settingsLoaded}
					// Persistence - Save callbacks
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
				/>

				{!orgScopedUserSessions.isLoading &&
				!orgScopedUserSessions.isFetching &&
				data?.length <= 0
					? noSessions(
							(unFilteredSessionCount.data ?? 0) > 0 &&
								(_sessionCount.data ?? 0) === 0,
						)
					: null}
			</PageLayout>
			<Outlet />

			{/* Revoke Sessions Confirmation Modal */}
			<RevokeSessionsModal
				isOpen={isRevokeModalOpen}
				setIsOpen={setIsRevokeModalOpen}
				sessionCount={selectedSessions.length}
				onConfirm={handleRevokeSessions}
				isLoading={revokeMutation.isPending}
			/>
		</>
	);
}
